import React from 'react';
import PropTypes from 'prop-types';
import { brandColors, textColors } from '../../tokens';

/**
 * Checkbox Component
 * 
 * Standardized checkbox component for forms.
 * Supports checked, unchecked, and indeterminate states.
 * 
 * @example
 * // Basic checkbox
 * <Checkbox
 *   checked={agreed}
 *   onChange={(e) => setAgreed(e.target.checked)}
 *   label="I agree to the terms"
 * />
 * 
 * @example
 * // Checkbox without label (provide aria-label)
 * <Checkbox
 *   checked={selected}
 *   onChange={(e) => setSelected(e.target.checked)}
 *   aria-label="Select item"
 * />
 */
const Checkbox = ({
  // Value and change handlers
  checked = false,
  onChange,
  onBlur,
  onFocus,
  
  // Checkbox attributes
  name,
  id,
  value,
  disabled = false,
  required = false,
  indeterminate = false,
  
  // Label
  label = '',
  labelPosition = 'right', // 'left' | 'right'
  
  // Sizing
  size = 'md', // 'sm' | 'md' | 'lg'
  
  // States
  error = '',
  
  // Styling
  className = '',
  labelClassName = '',
  
  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  
  // Additional props
  ...rest
}) => {
  const checkboxRef = React.useRef(null);
  
  // Set indeterminate state (can't be done via prop)
  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  
  // Determine state
  const hasError = Boolean(error);
  
  // Auto-generate ID for aria-describedby
  const errorId = id ? `${id}-error` : undefined;
  
  // Build aria-describedby
  let describedBy = ariaDescribedBy || '';
  if (hasError && errorId) describedBy = describedBy ? `${describedBy} ${errorId}` : errorId;
  
  // Size classes for checkbox
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };
  
  // Label text size classes
  const labelSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };
  
  // Base checkbox classes
  const baseClasses =
    'rounded border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-900';
  
  // State classes
  let stateClasses = '';
  if (disabled) {
    stateClasses = 'border-gray-300 dark:border-dark-700 bg-gray-100 dark:bg-dark-800 cursor-not-allowed';
  } else if (hasError) {
    stateClasses = 'border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 focus:ring-red-500 dark:focus:ring-red-400';
  } else {
    stateClasses = `border-gray-300 dark:border-dark-600 ${brandColors.primary} ${brandColors.focus} ${brandColors.borderHover} cursor-pointer`;
  }
  
  // Combine checkbox classes
  const checkboxClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    className,
  ].filter(Boolean).join(' ');
  
  // Combine label classes
  const combinedLabelClasses = [
    'select-none',
    labelSizeClasses[size],
    disabled ? 'text-gray-400 dark:text-dark-500 cursor-not-allowed' : `${textColors.secondary} cursor-pointer`,
    labelClassName,
  ].filter(Boolean).join(' ');
  
  // Checkbox element
  const checkboxElement = (
    <input
      ref={checkboxRef}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      name={name}
      id={id}
      value={value}
      disabled={disabled}
      required={required}
      className={checkboxClasses}
      aria-label={ariaLabel || (label ? undefined : 'Checkbox')}
      aria-describedby={describedBy || undefined}
      aria-invalid={ariaInvalid !== undefined ? ariaInvalid : hasError}
      {...rest}
    />
  );
  
  // Label element
  const labelElement = label ? (
    <span className={combinedLabelClasses}>
      {label}
    </span>
  ) : null;
  
  return (
    <div>
      {/* Checkbox with label */}
      <label className="inline-flex items-center">
        {labelPosition === 'left' && labelElement && (
          <>
            {labelElement}
            <div className="ml-3">
              {checkboxElement}
            </div>
          </>
        )}
        
        {labelPosition === 'right' && (
          <>
            {checkboxElement}
            {labelElement && (
              <div className="ml-2">
                {labelElement}
              </div>
            )}
          </>
        )}
      </label>
      
      {/* Error Message */}
      {hasError && (
        <p id={errorId} className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

Checkbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  
  name: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  indeterminate: PropTypes.bool,
  
  label: PropTypes.node,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  
  error: PropTypes.string,
  
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

export default Checkbox;
